/**
 * Centralized Tier Color Service
 * Single source of truth for all tier-based color management
 */

/**
 * Master tier color definitions
 * Used across all components, exports, and styling
 */
const TIER_COLOR_DEFINITIONS = {
  '1': { 
    light: '#ffeaea', 
    dark: '#dc3545',
    bg: 'FFFFEAEA',     // Excel format
    font: 'FFDC3545'    // Excel format
  },
  '2': { 
    light: '#fff8e1', 
    dark: '#ffc107',
    bg: 'FFFFF8E1',
    font: 'FFFFC107'
  },
  '3': { 
    light: '#e8f5e8', 
    dark: '#28a745',
    bg: 'FFE8F5E8',
    font: 'FF28A745'
  },
  'VIP': { 
    light: '#f3e5f5', 
    dark: '#9c27b0',
    bg: 'FFF3E5F5',
    font: 'FF9C27B0'
  },
  'Unknown': { 
    light: '#f8f9fa', 
    dark: '#6c757d',
    bg: 'FFF8F9FA',
    font: 'FF6C757D'
  },
  'N/A': { 
    light: '#f8f9fa', 
    dark: '#6c757d',
    bg: 'FFF8F9FA',
    font: 'FF6C757D'
  },
  'n/a': { 
    light: '#f8f9fa', 
    dark: '#6c757d',
    bg: 'FFF8F9FA',
    font: 'FF6C757D'
  }
};

/**
 * Get all tier colors in standard format
 * @returns {Object} Complete tier color definitions
 */
export const getTierColors = () => {
  return TIER_COLOR_DEFINITIONS;
};

/**
 * Get color for a specific tier
 * @param {string} tier - The tier value ('1', '2', '3', 'VIP', etc.)
 * @param {string} type - Color type ('light', 'dark', 'bg', 'font')
 * @returns {string} The color value
 */
export const getTierColor = (tier, type = 'dark') => {
  const tierData = TIER_COLOR_DEFINITIONS[tier] || TIER_COLOR_DEFINITIONS['Unknown'];
  return tierData[type];
};

/**
 * Get Excel-formatted tier colors for export functionality
 * @returns {Object} Tier colors in Excel format (bg/font properties)
 */
export const getExcelTierColors = () => {
  const excelColors = {};
  Object.keys(TIER_COLOR_DEFINITIONS).forEach(tier => {
    const colors = TIER_COLOR_DEFINITIONS[tier];
    excelColors[tier] = {
      bg: colors.bg,
      font: colors.font
    };
  });
  return excelColors;
};

/**
 * Generate CSS custom properties for tier colors
 * @returns {Object} CSS custom properties object
 */
export const generateTierColorCSSProperties = () => {
  const cssProps = {};
  Object.keys(TIER_COLOR_DEFINITIONS).forEach(tier => {
    const colors = TIER_COLOR_DEFINITIONS[tier];
    const tierKey = tier.toLowerCase().replace(/[^a-z0-9]/g, '');
    cssProps[`--tier-${tierKey}-light`] = colors.light;
    cssProps[`--tier-${tierKey}-dark`] = colors.dark;
  });
  return cssProps;
};

/**
 * Get all available tier keys
 * @returns {Array} Array of tier keys
 */
export const getTierKeys = () => {
  return Object.keys(TIER_COLOR_DEFINITIONS);
};

/**
 * Check if a tier exists in the color system
 * @param {string} tier - The tier to check
 * @returns {boolean} True if tier exists
 */
export const isTierValid = (tier) => {
  return tier in TIER_COLOR_DEFINITIONS;
};

// Default export for convenience
const TierColorService = {
  getTierColors,
  getTierColor,
  getExcelTierColors,
  generateTierColorCSSProperties,
  getTierKeys,
  isTierValid
};

export default TierColorService;
